import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import styles from './style.module.scss';
import { EyeIcon, ProjectNameIcon } from 'assets';
import { useSelector } from 'react-redux';
import GenericTable from 'shared/components/common/table/table';
import Heading from 'shared/components/common/heading/Heading';
import { GetShopifyProductQuery } from 'shared/services/product_service/ProductService';
import { SyncShopifyMutation } from 'shared/services/shopify_service/ShopifyService';
import OverlayLoading from 'shared/screen_loader/overlay_loader/OverlayLoading';
import SearchBar from 'shared/components/common/searchbar/SearchBar';
import ShopifyProductView from 'shared/modal/shopify_product_view/ShopifyProductView';

export default function ShopifyProducts() {
    const {
        theme: { theme }
    } = useSelector((state) => state.root);
    const [searchResults, setSearchResults] = useState([]);
    const [showDetail, setShowDetail] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);

    const { isLoading, data, refetch } = GetShopifyProductQuery();
    const { mutate: syncShopify, isLoading: syncLoading } = SyncShopifyMutation();

    const handleSync = () => {
        syncShopify(null, {
            onSuccess: () => {
                refetch();
            }
        });
    };

    const handleViewDetail = (product) => {
        setSelectedProduct(product);
        setShowDetail(true);
    };

    const columns = React.useMemo(
        () => [
            {
                Header: (
                    <div className={classNames(`${theme}FontColor w-100`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Product Name
                    </div>
                ),
                accessor: 'title',
                Cell: ({ row }) => <div className="w-100">{row.original?.title}</div>
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor w-100`)}>
                        Base SKU
                    </div>
                ),
                accessor: 'base_sku',
                Cell: ({ row }) => <div className="w-100">{row.original?.base_sku || 'N/A'}</div>
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor w-100`)}>
                        Vendor
                    </div>
                ),
                accessor: 'vendor',
                Cell: ({ row }) => <div className="w-100">{row.original?.vendor}</div>
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor w-100`)}>
                        Status
                    </div>
                ),
                accessor: 'status',
                Cell: ({ row }) => (
                    <div className="w-100">
                        <span className={classNames('badge', row.original?.status === 'active' ? 'bg-success' : 'bg-secondary')}>
                            {row.original?.status}
                        </span>
                    </div>
                )
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor w-100 text-end me-2`)}>
                        View
                    </div>
                ),
                accessor: 'view',
                Cell: ({ row }) => (
                    <div className="d-flex justify-content-end align-items-center">
                        <div className="mx-2">
                            <EyeIcon
                                className="pointer"
                                onClick={() => handleViewDetail(row.original)}
                            />
                        </div>
                    </div>
                )
            }
        ],
        [theme]
    );

    useEffect(() => {
        if (data?.data?.products) {
            setSearchResults(data.data.products);
        }
    }, [data]);

    const handleSearch = (filtered) => {
        setSearchResults(filtered);
    };

    return (
        <div className={classNames(styles.UserContainer, 'text-start')}>
            <div className="mb-3">
                <SearchBar
                    suggestionsSource={data?.data?.products || []}
                    onSearchChange={handleSearch}
                    customFilter={(item, query) => {
                        const q = query.toLowerCase();
                        return (
                            item.title?.toLowerCase().includes(q) ||
                            item.base_sku?.toLowerCase().includes(q) ||
                            item.vendor?.toLowerCase().includes(q)
                        );
                    }}
                />
            </div>

            <div className={classNames(styles.Header, 'd-flex justify-content-between align-items-center mb-3')}>
                <Heading heading={'Shopify Products'} headingStyle={styles.Heading} />
                <button
                    onClick={handleSync}
                    disabled={syncLoading}
                    className="d-flex align-items-center gap-2"
                    style={{
                        background: '#1a8a56',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '10px 20px',
                        fontWeight: 600,
                        cursor: syncLoading ? 'not-allowed' : 'pointer',
                        opacity: syncLoading ? 0.7 : 1,
                        transition: 'all 0.3s ease',
                        boxShadow: '0 4px 12px rgba(26, 138, 86, 0.25)',
                    }}
                    onMouseOver={(e) => {
                        if (!syncLoading) {
                            e.currentTarget.style.transform = 'translateY(-1px)';
                            e.currentTarget.style.boxShadow = '0 6px 16px rgba(26, 138, 86, 0.35)';
                        }
                    }}
                    onMouseOut={(e) => {
                        if (!syncLoading) {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(26, 138, 86, 0.25)';
                        }
                    }}
                >
                    <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{
                            animation: syncLoading ? 'spin 1s linear infinite' : 'none',
                            transformOrigin: 'center'
                        }}
                    >
                        <path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                    </svg>
                    {syncLoading ? 'Syncing...' : 'Sync Products'}
                </button>
            </div>

            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
            <GenericTable columns={columns} data={searchResults || []} TDTextStyle={classNames(styles.TDTextStyle)} TableWrapperStyle={classNames(styles.Table)} />

            <ShopifyProductView
                show={showDetail}
                product={selectedProduct}
                handleClose={() => setShowDetail(false)}
            />

            {(isLoading || syncLoading) && <OverlayLoading />}
        </div >
    );
}

